<template>
  <v-card>
    <v-card-title :class="headerColorClass">
      <span class="headline white--text">
        {{ $t('createTicketBooks') }}
      </span>
      <v-spacer/>
      <Icon
      iconColor="white"
      icon="mdi-close"
      :small="false"
      large
      :tooltipText="$t('close')"
      @icon-clicked="$emit('close')"/>
    </v-card-title>
    <v-card-text>
      <FormWrapper
      :buttonText="buttonText"
      :buttonColor="headerColorClass"
      :disabled="disabled"
      @submit="createTicketBooks"
      strongValidation
      ref="form">
        <v-container>
          <slot/>
          <v-row>
            <v-col cols="12" sm="4">
              <v-text-field
              ref="startValue"
              :label="$t('firstValue')"
              color="black"
              type="number"
              hide-spin-buttons
              v-model="startValueString"
              :rules="[rules.required, rules.nonzeroPositiveIntegerOrBlank, checkStartValue]"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
              ref="endValue"
              :label="$t('lastValue')"
              color="black"
              type="number"
              hide-spin-buttons
              v-model="endValueString"
              :rules="[rules.required, rules.nonzeroPositiveIntegerOrBlank, checkEndValue]"
              @blur="validateFields"
              />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
              ref="bookSize"
              type="number"
              hide-spin-buttons
              :label="$t('bookSize')"
              :suffix="pageSuffix"
              v-model="bookSizeInput"
              :rules="[rules.nonzeroPositiveIntegerOrBlank, checkPageCount]">
                <template #append>
                  <Icon
                  v-if="canRestoreBooks"
                  icon="mdi-restore"
                  :tooltipText="$t('restoreTo', { p: pageRange })"
                  @icon-clicked="restoreBookSize"
                  :small="false"/>
                </template>
              </v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-text-field
              ref="bookName"
              :label="multipleBooks ? $t('prefix') : $t('title')"
              color="black"
              v-model="ticketBook.name"
              maxLength="64"
              :rules="[rules.required]"
              :hint="multipleBooks ? $t('ticketBookPrefixHint', { prefix: ticketBook.name || $t('prefix') }) : ''"
              :persistentHint="multipleBooks"/>
            </v-col>
          </v-row>
        </v-container>
      </FormWrapper>
    </v-card-text>
  </v-card>
</template>

<script>
import fieldRules from '@/utils/rules.js'
import { TicketBookStatus } from '@/utils/Enumerations.js'
import { TicketBookConstants } from '@/utils/constants.js'
export default {
  name: 'TicketBookForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue')
  },

  props: {
    ticketBooks: {
      type: Array,
      default: () => []
    },
    headerColorClass: {
      type: String,
      default: 'primary'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    rules: fieldRules.rules,
    ticketBook: {
      name: '',
      pages: []
    },
    startValueString: '',
    endValueString: '',
    bookSizeString: '',
    hasSetBookSize: false,
    multipleBooks: false
  }),

  watch: {
    startValueString () {
      this.validateFields()
    },

    endValueString () {
      this.validateFields()
    },

    validRangeMultipleBooks (multipleBooks) {
      if (multipleBooks === undefined || multipleBooks === this.multipleBooks) return
      this.multipleBooks = multipleBooks
      this.ticketBook.name = ''
    }
  },

  created () {
    this.$nextTick(_ => this.$refs.startValue?.focus())
  },

  computed: {
    startValue () {
      return parseInt(this.startValueString) || 0
    },

    endValue () {
      return parseInt(this.endValueString) || 0
    },

    hasValidRange () {
      return this.isEntered(this.startValueString) && this.isEntered(this.endValueString) && this.startValue <= this.endValue
    },

    pageRange () {
      if (!this.hasValidRange) return 0
      return this.endValue - this.startValue + 1
    },

    bookSizeInput: {
      get () {
        if (this.hasSetBookSize) return this.bookSizeString
        return this.hasValidRange ? String(this.pageRange) : ''
      },
      set (val) {
        this.bookSizeString = val
        this.hasSetBookSize = true
      }
    },

    bookSize () {
      return this.hasSetBookSize ? parseInt(this.bookSizeString) || 0 : this.pageRange
    },

    validRangeMultipleBooks () {
      if (!this.hasValidRange) return undefined
      return this.bookSize !== this.pageRange
    },

    pageSuffix () {
      if (!this.isEntered(this.bookSizeInput)) return undefined
      return this.bookSize === 1 ? 'page' : 'pages'
    },

    canRestoreBooks () {
      return this.hasSetBookSize && this.hasValidRange && this.bookSize !== this.pageRange
    },

    bookCount () {
      return this.pageRange / this.bookSize
    },

    buttonText () {
      if (this.bookCount === Infinity) return this.$t('createOneTicketBook')
      return this.bookCount > 1 ? this.$t('createMultipleTicketBooks', { n: parseInt(this.bookCount) }) : this.$t('createOneTicketBook')
    }
  },

  methods: {
    createTicketBooks () {
      const overlappingTicketBook = this.ticketBooks.find(tb => tb.status !== TicketBookStatus.Inactive.value && tb.firstPage <= this.endValue && tb.lastPage >= this.startValue)
      if (overlappingTicketBook != null) {
        this.setSnackError(this.$t('overlappingTicketBooksFound', { name: overlappingTicketBook.name }))
        return
      }

      const maximumPageRange = 5000
      if (this.pageRange > maximumPageRange) {
        this.setSnackError(this.$t('ticketBookPageRangeExceeded', { range: maximumPageRange }))
        return
      }

      if (this.bookSize > TicketBookConstants.MaxNewPagesPerBook) {
        this.setSnackError(this.$t('ticketBookPageCountExceeded', { max: TicketBookConstants.MaxNewPagesPerBook }))
        return
      }

      const count = this.pageRange / this.bookSize
      const books = Array.from({ length: count }, (_, idx) => {
        const name = count === 1
          ? this.ticketBook.name
          : `${this.ticketBook.name} ${idx + 1}`
        return {
          name: name,
          pages: Array.from({ length: this.bookSize }, (__, i) => ({ pageNumber: this.startValue + idx * this.bookSize + i })),
          totalPageCount: this.bookSize,
          availablePageCount: this.bookSize,
          status: TicketBookStatus.Available.value,
          firstPage: this.startValue + idx * this.bookSize,
          lastPage: this.startValue + idx * this.bookSize + this.bookSize - 1
        }
      })

      this.$emit('create-ticket-books', books)
    },

    isEntered (value) {
      return value != null && value !== ''
    },

    checkStartValue () {
      if (!this.isEntered(this.endValueString)) return true
      return this.startValue <= this.endValue || this.$t('startValueMustBeLessThanOrEqualToEndValue')
    },

    checkEndValue () {
      if (!this.isEntered(this.startValueString)) return true
      return this.endValue >= this.startValue || this.$t('startValueMustBeLessThanOrEqualToEndValue')
    },

    checkPageCount () {
      if (!this.hasValidRange) return true
      return this.pageRange % this.bookSize === 0 || this.$t('pageCountMustDividePageRange')
    },

    validateFields () {
      this.$refs.startValue.validate()
      this.$refs.endValue.validate()
      this.$refs.bookSize.validate()
    },

    restoreBookSize () {
      this.hasSetBookSize = false
      this.bookSizeString = ''
      this.$refs.bookName.focus()
    }
  }
}
</script>

<style scoped>
:deep .v-text-field__suffix {
  font-size: 0.9rem;
  line-height: 1;
  opacity: 0.9;
}
</style>
